import moment from "moment";

import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LifeStageDatePrecisionEnum } from "./LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "./LifeStageDateStatusEnum";

class DateTimeWithPrecision {
    public text: string;
    public value: Date;
    public lang: string;
    public precision: LifeStageDatePrecisionEnum;
    public isFromDateSelector: boolean = false;
    public status: LifeStageDateStatusEnum;

    constructor(newText: string, oldText: string, expectedPrecision: LifeStageDatePrecisionEnum = null) {
        this.text = oldText;

        if (newText == null)
            return;

        var lang = sessionStore.userLanguageCode;
        var currentPrecision = null;
        var year: number = null;
        var month: number = null;
        var day: number = null;

        var hasSeparators = newText.indexOf("//") > -1 || newText.indexOf("\\") > -1 || newText.indexOf("-") > -1;
        var terms = newText.trim().replace(/ |\/|\\/g, "-").split("-"); // Replace "/" "\" with "-"
        var cleanText = newText.replace(/ |-|\/|\\/g, "");

        if (terms.length > 3) {
            if (newText != oldText)
                return new DateTimeWithPrecision(oldText, oldText, expectedPrecision);
            else
                return;
        }

        // date is potentially typed without separators, try to parse
        if ((terms != null && terms.length != 3) || !hasSeparators) {
            terms = [];
            let txt = cleanText.trim();
            let len = txt.length;

            if (len >= 4)
                terms.push(txt.substr(0, 4));

            if (len > 4)
                terms.push(len >= 6 ? txt.substr(4, 2) : txt.substr(4, 1));

            if (len > 6)
                terms.push(len >= 8 ? txt.substr(6, 2) : txt.substr(6, 1));

            if (len > 8) {
                if (newText != oldText)
                    return new DateTimeWithPrecision(oldText, oldText, expectedPrecision);
                else
                    return;
            }
        }

        var hasYear: boolean = terms.length >= 1;
        var hasMonth: boolean = terms.length >= 2;
        var hasDay: boolean = terms.length == 3;

        var _date = null;
        this.status = LifeStageDateStatusEnum.Valid;

        if (hasYear) {
            currentPrecision = LifeStageDatePrecisionEnum.Year; // Year

            if (terms[0].length < 4) {
                this.status = LifeStageDateStatusEnum.ValidWithExtraChar;
            }
            else {
                year = Number(terms[0]);
                this.status = this.IsValidYear(year) ? LifeStageDateStatusEnum.Valid : LifeStageDateStatusEnum.Invalid;
            }

            if (this.status == LifeStageDateStatusEnum.Valid && !hasMonth)
                _date = new Date(year, 0, 1);
        }
        if (this.status == LifeStageDateStatusEnum.Valid && hasMonth) {
            currentPrecision = LifeStageDatePrecisionEnum.MonthYear; // Month

            if (terms[1].length < 2) {
                this.status = LifeStageDateStatusEnum.ValidWithExtraChar;
            }
            else {
                month = Number(terms[1]);
                this.status = this.IsValidMonth(month) ? LifeStageDateStatusEnum.Valid : LifeStageDateStatusEnum.Invalid;
            }

            if (this.status == LifeStageDateStatusEnum.Valid && !hasDay)
                _date = new Date(year, month - 1, 1);
        }
        if (this.status == LifeStageDateStatusEnum.Valid && hasDay) {
            currentPrecision = LifeStageDatePrecisionEnum.Day; // Day

            if (terms[2].length < 2) {
                this.status = LifeStageDateStatusEnum.ValidWithExtraChar;
            }
            else if (terms[2].length > 2) {
                if (newText != oldText)
                    return new DateTimeWithPrecision(oldText, oldText, expectedPrecision);
                else
                    return;
            }
            else {
                day = Number(terms[2]);
                this.status = this.IsValidDay(day, month, year) ? LifeStageDateStatusEnum.Valid : LifeStageDateStatusEnum.Invalid;
            }

            if (this.status == LifeStageDateStatusEnum.Valid)
                _date = new Date(year, month - 1, day);
        }

        if (_date != null) { // Accept Changes with date
            this.value = this.status == LifeStageDateStatusEnum.Valid ? _date : null;
            this.text = newText;
            this.precision = currentPrecision;
            this.lang = lang;

            if (this.status == LifeStageDateStatusEnum.Valid && expectedPrecision != null && (expectedPrecision < currentPrecision || currentPrecision == null))
                this.status = LifeStageDateStatusEnum.ValidWithExtraChar;
        }
        else if (!isNaN(Number(cleanText))) { // just Validate he is typing numeric
            this.status = this.status != LifeStageDateStatusEnum.Invalid ? LifeStageDateStatusEnum.ValidWithExtraChar : LifeStageDateStatusEnum.Invalid;
            this.text = newText; // Accept Changes
        }
    }

    public IsValidYear(year: number): boolean {
        return year != null && !isNaN(year) && year >= 1855 && year <= 3000;
    }
    public IsValidMonth(month: number): boolean {
        return month != null && !isNaN(month) && month >= 1 && month <= 12;
    }
    public IsValidDay(day: number, month: number, year: number): boolean {
        return moment(year + " " + month + " " + day, "YYYY MM DD").isValid();
    }

    public get isDayPrecision(): boolean {
        return this.precision == LifeStageDatePrecisionEnum.DayApprox
            || this.precision == LifeStageDatePrecisionEnum.Day;
    }

    public get isMonthOrYearPrecision(): boolean {
        return this.precision == LifeStageDatePrecisionEnum.MonthYear
            || this.precision == LifeStageDatePrecisionEnum.Year;
    }

    public get isYearPrecision(): boolean {
        return this.precision == LifeStageDatePrecisionEnum.Year;
    }

    public get isMonthYearPrecision(): boolean {
        return this.precision == LifeStageDatePrecisionEnum.MonthYear;
    }

    public getFormattedDate(): string {
        let dateFormat = "yyyy-MM-dd";

        if (this.isYearPrecision)
            dateFormat = "yyyy";

        if (this.isMonthYearPrecision)
            dateFormat = "yyyy-MM";

        return this.value ? LuxonDateTimeParser.parse(this.value).toFormat(dateFormat) : "";
    }

    public static getToday(): DateTimeWithPrecision {
        return new DateTimeWithPrecision(LuxonDateTimeParser.parse(new Date()).toFormat("yyyy-MM-dd"), "", LifeStageDatePrecisionEnum.Day);
    }

    public static getDate(date: Date | null | string): DateTimeWithPrecision {
        if (!date) {
            return new DateTimeWithPrecision(null, "", LifeStageDatePrecisionEnum.Day);
        }

        return new DateTimeWithPrecision(LuxonDateTimeParser.parse(date).toFormat("yyyy-MM-dd"), "", LifeStageDatePrecisionEnum.Day);
    }

    public static getEmptyDate(): DateTimeWithPrecision {
        return new DateTimeWithPrecision(null, "", LifeStageDatePrecisionEnum.Day);
    }
}

export { DateTimeWithPrecision };
